<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import InlineDateTimePicker from '@/components/Atoms/InlineDateTimePicker.vue'
import moment from 'moment'

import { newDate } from '@/filtersNew'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '@/components/icon/IconCamera.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue'
import IconClock from '@/components/icon/IconClock.vue'
import IconReplace from '@/components/icon/IconReplace.vue'
import IconBroadcast from '@/components/icon/IconBroadcast.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Alert from '@/components/Atoms/Alert.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
	mediaPulse: {
		type: Object,
		default: null,
	},
	limitsExceeded: {
		type: Boolean,
		default: false,
	},
	limitsLoading: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['enable-stream'])

const store = useStore()
const { notify } = useNotification()

const playout = computed(() => store.getters['Streams/tvPlayout'])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const playlist = computed(() => store.getters['Playout/playlist'] || [])
const playoutStartTime = computed(() => store.getters['Playout/playoutStartTime'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const maxPlayoutLoopDate = computed(() => store.getters['User/maxTvPlayoutLoopDate'])
const isPlayoutInThePast = computed(() => store.getters['Playout/isPlayoutInThePast'])
const hasPlayoutLoopMode = computed(() => !!store.getters['User/hasPlayoutLoopMode'])
const loopModeUpgradeTooltip = 'Upgrade Plan — Loop mode isn’t available on the Starter plan.'
// Global unsaved-changes flag, so edits made while these controls were unmounted
// (e.g. adding a file from the Files tab) still enable "Set Schedule".
const scheduleDirty = computed(() => store.getters['Playout/scheduleDirty'])

// Destination
const destinationDropdown = ref()
const selectedDestinationStream = computed(() =>
	playoutConfig.value?.destination
		? allInOneStreams.value.find(
			(stream) => stream._id === playoutConfig.value?.destination,
		)
		: null,
)
const destinationName = computed(() =>
	selectedDestinationStream.value?.name || 'Choose destination',
)

const isDestinationDisabled = (streamKey) => {
	const stream = allInOneStreams.value.find((s) => s.key === streamKey)
	return (
		stream?.playoutDestination ||
		!!playlist.value?.some((item) => item.sourceLocation === streamKey)
	)
}

const destinationTooltip = (streamKey) => {
	const stream = allInOneStreams.value.find((s) => s.key === streamKey)
	if (stream?.playoutDestination) {
		return 'This stream is destination for another TV Playout and cannot be selected.'
	}
	if (playlist.value?.some((item) => item.sourceLocation === streamKey)) {
		return 'This stream added on the playlist, it cannot be set as destination'
	}
	return ''
}

const setDestination = async (id) => {
	await store.dispatch('Playout/setDestination', id)
	await store.dispatch('Playout/toggleDestination', true)
	destinationDropdown.value?.toggleDropdown()
	await applyChanges('destination updated')
}

const showRemoveDestinationModal = () => {
	showModal('playout-v2-remove-destination' + playout.value._id)
}

const removeDestination = async () => {
	await store.dispatch('Playout/setDestination', null)
	await store.dispatch('Playout/toggleDestination', false)
	// Treat destination removal as a pending change so the user can push it to the
	// backend via "Set Schedule" (instead of auto-saving and locking the button).
	markDirty()
}

// Mode toggle (schedule / loop)
const newMode = ref(null)

const showToggleModeModal = (mode) => {
	if (mode === 'loop' && !hasPlayoutLoopMode.value) return
	if (playoutConfig.value?.mode === mode) return
	newMode.value = mode
	if (playlist.value?.length > 0) {
		showModal('playout-v2-change-mode' + playout.value._id)
	} else {
		applyMode()
	}
}

const applyMode = () => {
	if (!newMode.value) return
	store.dispatch('Playout/setMode', newMode.value)
	newMode.value = null
}

// Track unsaved changes (sources / date) — mirrors v1 PlayoutSetSchedule logic
const isDirty = ref(false)
const markDirty = () => {
	isDirty.value = true
}

// Start time
const minStartTime = computed(() => newDate(moment().subtract(1, 'day').valueOf()))

const minLoopEndDate = computed(() =>
	newDate(moment(playoutStartTime.value).add(1, 'days').valueOf()),
)
const maxLoopEndDate = computed(() =>
	newDate(
		moment(playoutStartTime.value)
			.add(maxPlayoutLoopDate.value || 7, 'days')
			.valueOf(),
	),
)

// Single combined date + time field per boundary. Both edit the same store Date.
const startDateTimeModel = computed({
	get() {
		return newDate(playoutStartTime.value)
	},
	set(value) {
		if (!(value instanceof Date) || isNaN(value)) return
		store.dispatch('Playout/changeStartTime', value)
		markDirty()
	},
})

const loopEndBaseMs = computed(() => {
	const cfg = playoutConfig.value?.settings?.loopEndDate
	if (cfg) return moment(cfg).valueOf()
	return moment(playoutStartTime.value)
		.add(maxPlayoutLoopDate.value || 7, 'days')
		.valueOf()
})
const loopEndDateTimeModel = computed({
	get() {
		return newDate(loopEndBaseMs.value)
	},
	set(value) {
		if (!(value instanceof Date) || isNaN(value)) return
		store.dispatch('Playout/setLoopMode', value)
		markDirty()
	},
})

const clearLoopTimes = () => {
	store.dispatch('Playout/changeStartTime', new Date())
	store.dispatch('Playout/setLoopMode', null)
}

// Set Schedule
const saving = ref(false)

let lastSyncSeq = store.getters['Playout/serverSyncSeq']
watch(
	() => store.state?.Playout?.playoutConfig,
	(configNew, configOld) => {
		const syncSeq = store.getters['Playout/serverSyncSeq']
		if (syncSeq !== lastSyncSeq) {
			lastSyncSeq = syncSeq
			return
		}
		// Any change to the config after the initial load means there are unsaved
		// changes (adding/removing sources, reordering, date/destination edits...).
		// The initial load is guarded by configOld being null. Validity of those
		// changes is handled separately by setScheduleBlockedReason, so we must NOT
		// gate this on playlist length — removing the last source still counts as a
		// change that needs to be persisted.
		if (configOld) {
			isDirty.value = true
		}
	},
	{ deep: true },
)

// Human-readable reason explaining why "Set Schedule" is blocked (empty = not blocked).
// Used both for the disabled state and the tooltip shown on hover.
const setScheduleBlockedReason = computed(() => {
	if (saving.value) return ''
	if (props.limitsLoading) {
		return 'Syncing schedule…'
	}
	// Plan limits exceeded (file count / size / duration) — same condition that
	// shows the warning banner. The schedule cannot be saved until resolved.
	if (props.limitsExceeded) {
		return 'Your TV Playout exceeds your plan limits. Remove some files or upgrade your plan to set a schedule.'
	}
	// Loop window must be valid before it can be saved.
	if (isLoopDatesInvalid.value) {
		return "The start date and time must be earlier than the loop end date."
	}
	// Pending changes take priority: any edit (add/remove/reorder/date/destination)
	// should re-enable the button so the new config can be pushed to the backend.
	if (!isDirty.value && !scheduleDirty.value) {
		return 'No changes to apply.'
	}
	const hasInvalidLive = playlist.value?.some(
		(el) => el.sourceType === 'live' && el.endTime - el.startTime < 10 * 60 * 1000,
	)
	if (hasInvalidLive) {
		return 'Live sources must be scheduled for at least 10 minutes.'
	}
	return ''
})

// "Set Schedule" is enabled only when there is a valid, unsaved configuration.
// (A disabled/idle playout has no unsaved changes on load, so it stays blocked
//  until the user actually edits sources or the date.)
const isSetScheduleDisabled = computed(
	() => saving.value || !!setScheduleBlockedReason.value,
)

const goLive = async () => {
	if (saving.value) return
	saving.value = true
	try {
		await store.dispatch('Playout/setSchedule', playout.value._id)
		track(EVENTS.TV_PLAYOUT_SCHEDULE_CONFIGURED, {
			playoutId: playout.value._id,
			mode: playoutConfig.value?.mode,
			playlistItemCount: playlist.value?.length || 0,
		})
		emit('enable-stream', true)
		notify({ group: 'success', text: `${playout.value.name} was set` })
		isDirty.value = false
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Failed to set schedule' })
	} finally {
		saving.value = false
	}
}

const applyChanges = async (text) => {
	await store.dispatch('Playout/setSchedule', playout.value._id)
	isDirty.value = false
	notify({
		group: 'success',
		text: `${playout.value.name} ${text}`,
	})
}

const isScheduleMode = computed(() => playoutConfig.value?.mode === 'schedule')
const isLoopMode = computed(() => playoutConfig.value?.mode === 'loop')

// Loop window validity: the start date/time must be earlier than the loop end
// date. Editing the start time has no upper bound, so a user can push it past
// the end date — guard against that with a warning + blocked "Set Schedule".
const loopEndDateMs = computed(() => {
	const cfg = playoutConfig.value?.settings?.loopEndDate
	if (cfg) return moment(cfg).valueOf()
	return moment(playoutStartTime.value)
		.add(maxPlayoutLoopDate.value || 7, 'days')
		.valueOf()
})
const isLoopDatesInvalid = computed(
	() => isLoopMode.value && moment(playoutStartTime.value).valueOf() >= loopEndDateMs.value,
)

// The schedule/loop is "over" when there's nothing left to broadcast:
//  - schedule mode: the start time + total playlist length is already in the past
//  - loop mode: the loop's end date is in the past (a past start just resumes
//    mid-loop, so it isn't a problem on its own)
const scheduleEnded = computed(() => {
	if (isLoopMode.value) return loopEndDateMs.value < moment().valueOf()
	return isPlayoutInThePast.value
})
const scheduleEndedText = computed(() =>
	isLoopMode.value
		? 'This loop has already ended — its end date is in the past. Choose a later end date to keep broadcasting.'
		: 'This schedule has already finished playing — your start time plus the playlist length is in the past. Choose a later start time to broadcast.',
)

const isPlayoutLive = computed(
	() => !!playout.value?.enabled && !!props.mediaPulse?.alive,
)

const playlistItemsCount = computed(() => playlist.value?.length || 0)

const broadcastingLabel = computed(() => {
	const mode = playoutConfig.value?.mode === 'loop' ? 'loop' : 'schedule'
	const count = playlistItemsCount.value
	const itemWord = count === 1 ? 'item' : 'items'
	return `Broadcasting in ${mode} mode · ${count} ${itemWord} in playlist`
})

// Sentence-case + context-aware: it "updates" an already-live channel, otherwise "sets" it.
const setScheduleLabel = computed(() => (isPlayoutLive.value ? 'Update schedule' : 'Set schedule'))

// Match sm control height (h-8 neighbors): group !p-0.5 + h-7 tabs = 32px.
// Bump surfaces so the group reads on the surrounding bg-surface-2 card
// (group: surface-3, active tab: surface-4).
const modeTabClasses = (active, disabled = false) => [
	'h-7 px-2 justify-center hover:no-underline rounded text-nowrap leading-none text-tiny flex items-center w-full',
	disabled
		? 'bg-surface-3 text-surface-6 cursor-not-allowed opacity-60'
		: active
			? 'bg-surface-4 hover:bg-surface-4 text-surface-9 hover:text-white'
			: 'bg-surface-3 hover:bg-surface-4/40 text-surface-9 hover:text-white',
].join(' ')

// keep mode tab in sync if user changes via modal cancel
watch(
	() => playoutConfig.value?.mode,
	() => {
		newMode.value = null
	},
)
</script>

<template>
	<div v-if="playoutConfig" class="bg-surface-2 rounded-lg p-3 sm:p-4 flex flex-col gap-3">
		<!-- Mobile: destination → mode → date → helper → Set schedule.
		     Desktop: mode → date → destination → Set schedule, helper on its own row. -->
		<div class="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-x-4 gap-y-3">
			<div class="flex flex-col gap-1.5 w-full sm:w-auto sm:min-w-[180px] sm:max-w-[280px] order-1 sm:order-3">
				<div class="hidden sm:flex items-center gap-1">
					<label class="text-tiny font-medium text-surface-8">Output destination</label>
					<Tooltip content="The livestream this channel is broadcast to.">
						<icon-base class="text-surface-6 hover:text-surface-8 transition-colors" width="14" height="14">
							<icon-info-small />
						</icon-base>
					</Tooltip>
				</div>
				<DropDownMenu
					ref="destinationDropdown"
					type="flat"
					listWidth="w-full sm:w-[240px]"
					listClasses="!max-w-[calc(100vw-3rem)] sm:!max-w-[280px]"
				>
					<template #toggle-button>
						<icon-base class="mr-2 shrink-0">
							<icon-camera />
						</icon-base>
						<span class="truncate min-w-0 flex-1 text-left" :title="destinationName">
							{{ destinationName }}
						</span>
						<span
							v-if="selectedDestinationStream"
							class="ml-2 shrink-0 inline-flex items-center justify-center text-surface-7 hover:text-error-light"
							title="Remove destination"
							@click.stop="showRemoveDestinationModal"
						>
							<icon-base>
								<icon-bin />
							</icon-base>
						</span>
						<icon-base class="ml-2 shrink-0">
							<icon-chevron-down />
						</icon-base>
					</template>
					<template v-if="allInOneStreams.length > 0" #menu-items>
						<template v-for="liveStream in allInOneStreams" :key="liveStream._id">
							<DropDownMenuItem
								v-if="!isDestinationDisabled(liveStream.key)"
								customClasses="flex items-center gap-2 w-full h-8 px-3 hover:bg-surface-3 text-surface-text-1 hover:text-white leading-none text-tiny"
								@click="setDestination(liveStream._id)"
							>
								<icon-base class="shrink-0">
									<icon-camera />
								</icon-base>
								<span class="truncate min-w-0 flex-1 text-left" :title="liveStream.name">
									{{ liveStream.name }}
								</span>
								<icon-base
									v-if="liveStream._id === playoutConfig?.destination"
									class="shrink-0 text-success"
								>
									<icon-checkmark />
								</icon-base>
							</DropDownMenuItem>
							<DropDownMenuItem
								v-else
								isText
								disabled
								customClasses="flex items-center w-full h-8 px-3 text-surface-7 leading-none text-tiny"
							>
								<Tooltip :content="destinationTooltip(liveStream.key)" wrapperClass="flex w-full min-w-0">
									<div class="flex items-center w-full min-w-0 gap-2">
										<icon-base class="shrink-0">
											<icon-camera />
										</icon-base>
										<span class="truncate min-w-0 flex-1 text-left" :title="liveStream.name">
											{{ liveStream.name }}
										</span>
									</div>
								</Tooltip>
							</DropDownMenuItem>
						</template>
					</template>
					<template v-else #menu-items>
						<DropDownMenuItem
							isText
							customClasses="flex items-center w-full h-8 px-3 text-surface-8 leading-none text-tiny"
						>
							Please create a Live Stream
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</div>

			<div class="flex flex-col gap-1.5 w-full sm:w-auto order-2 sm:order-1">
				<div class="hidden sm:flex items-center gap-1">
					<label class="text-tiny font-medium text-surface-8">Playback mode</label>
					<Tooltip content="Schedule: play each source at an exact date & time. Loop: replay the whole playlist on repeat.">
						<icon-base class="text-surface-6 hover:text-surface-8 transition-colors" width="14" height="14">
							<icon-info-small />
						</icon-base>
					</Tooltip>
				</div>
				<TabGroup type="custom" custom-classes="inline-flex flex-wrap items-center p-0.5 rounded bg-surface-3 w-full sm:w-auto">
					<TabItem
						centered
						size="sm"
						:isActive="isScheduleMode"
						:onClick="() => showToggleModeModal('schedule')"
						:customClasses="modeTabClasses(isScheduleMode)"
						class="basis-1/2 sm:basis-auto"
					>
						<icon-base class="shrink-0 mr-1.5" width="14" height="14">
							<icon-clock />
						</icon-base>
						Schedule
					</TabItem>
					<li class="basis-1/2 sm:basis-auto">
						<Tooltip
							:active="!hasPlayoutLoopMode"
							:content="loopModeUpgradeTooltip"
							wrapper-class="block w-full"
							popper-class="max-w-[260px] text-sm"
						>
							<FormButton
								size="sm"
								:customClasses="modeTabClasses(isLoopMode, !hasPlayoutLoopMode)"
								@click="showToggleModeModal('loop')"
							>
								<icon-base class="shrink-0 mr-1.5" width="14" height="14">
									<icon-replace />
								</icon-base>
								Loop
							</FormButton>
						</Tooltip>
					</li>
				</TabGroup>
			</div>

			<div class="flex flex-col gap-1.5 w-full sm:w-auto sm:min-w-[200px] order-3 sm:order-2">
				<label class="text-tiny font-medium text-surface-8">{{ isLoopMode ? 'Broadcast window' : 'Start date & time' }}</label>
				<div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
					<InlineDateTimePicker
						v-model="startDateTimeModel"
						:min-date="minStartTime"
						size="sm"
						class="w-full sm:w-[185px]"
					/>
					<template v-if="isLoopMode">
						<div class="flex items-center gap-2 w-full sm:w-auto">
							<span class="text-tiny text-surface-7 shrink-0">to</span>
							<InlineDateTimePicker
								v-model="loopEndDateTimeModel"
								:min-date="minLoopEndDate"
								:max-date="maxLoopEndDate"
								size="sm"
								time-label="End time"
								class="min-w-0 flex-1 sm:flex-none sm:w-[185px]"
							/>
						</div>
					</template>
				</div>
			</div>

			<div class="flex flex-wrap items-center gap-x-2 gap-y-1 order-4 w-full sm:order-5 sm:basis-full">
				<p class="text-tiny text-surface-7 m-0">
					<template v-if="isScheduleMode">Schedule mode plays each source at the exact date and time you set.</template>
					<template v-else>Loop mode replays your playlist on repeat between the start and end dates.</template>
				</p>
			</div>

			<Tooltip
				wrapperClass="flex w-full sm:w-auto order-5 sm:order-4 sm:ml-auto sm:shrink-0 [&>.v-popper]:w-full sm:[&>.v-popper]:w-auto"
				:content="setScheduleBlockedReason"
				:active="!!setScheduleBlockedReason"
			>
				<FormButton
					type="main"
					class="!w-full justify-center sm:!w-auto"
					:is-loading="saving"
					:disabled="isSetScheduleDisabled"
					@click="goLive"
				>
					<icon-base class="mr-2 shrink-0" fill="none">
						<icon-broadcast />
					</icon-base>
					{{ setScheduleLabel }}
				</FormButton>
			</Tooltip>
		</div>

		<div v-if="isPlayoutLive" class="flex items-center gap-2">
			<span class="inline-block size-2.5 rounded-full shrink-0 bg-success" />
			<span class="text-tiny font-medium text-success">{{ broadcastingLabel }}</span>
		</div>

		<Alert v-if="isLoopDatesInvalid" class="w-full" type="warning"
			text="The start date and time can't be later than the loop end date. Adjust the dates to set the schedule." />

		<Alert v-if="scheduleEnded" class="w-full" type="warning" :text="scheduleEndedText" />

		<ConfirmModal :modalId="'playout-v2-change-mode' + playout?._id"
			:message="`Are you sure you want to switch to ${newMode} mode?`"
			body="We will remove all of your existing content on this playout." @modal-confirm="applyMode"
			@modal-cancel="newMode = null" />

		<ConfirmModal :modalId="'playout-v2-remove-destination' + playout?._id"
			message="Are you sure you want to remove the destination?" modal-type="danger"
			@modal-confirm="removeDestination" />
	</div>
</template>

<style scoped></style>
